import React, { useState, useEffect } from "react";
import { Search, ShoppingCart, Menu, X, ChevronDown, ChevronRight, Globe } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { onSnapshot, collection, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { getCartCount } from "../utils/cart";
import logo from "../assets/fecoslogo.png";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import { normalizeProduct } from "../utils/product";

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const toggleLanguage = () => {
    const newLang = language === 'tr' ? 'en' : 'tr';
    i18n.changeLanguage(newLang);
    localStorage.setItem("appLanguage", newLang);
    document.documentElement.lang = newLang; // immediate update
  };

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileSubmenu, setActiveMobileSubmenu] = useState(null);

  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [dynamicCategories, setDynamicCategories] = useState([]);

  const location = useLocation();
  const navigate = useNavigate();

  // Scroll Animation Hooks
  const { scrollY } = useScroll();
  const navHeight = useTransform(scrollY, [0, 50], ["5.5rem", "3.75rem"]);
  const navBackground = useTransform(
    scrollY,
    [0, 50],
    ["rgba(253, 251, 247, 0.8)", "rgba(253, 251, 247, 0.7)"] // Warm off-white glass
  );
  const navBackdropBlur = useTransform(scrollY, [0, 50], ["10px", "20px"]);
  const navShadow = useTransform(scrollY, [0, 50], ["0px 0px 0px rgba(0,0,0,0)", "0px 4px 20px rgba(0,0,0,0.03)"]);
  const logoHeight = useTransform(scrollY, [0, 50], ["64px", "40px"]); // Slightly smaller for better mobile fit

  // Ürün Listeleme (search & categories)
  useEffect(() => {
    const q = query(collection(db, "products"), where("isActive", "==", true));
    const unsub = onSnapshot(q, (snap) => {
      const normalized = snap.docs.map(normalizeProduct);
      setProducts(normalized);

      const categories = [...new Set(normalized.map(p => p.category).filter(Boolean))];
      setDynamicCategories(categories.map(c => ({
        name: c,
        slug: c
      })));
    });
    return () => unsub();
  }, []);

  // Search önerileri
  useEffect(() => {
    if (!searchTerm.trim()) return setSuggestions([]);
    setSuggestions(
      products
        .filter((p) =>
          p.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .slice(0, 5)
    );
  }, [searchTerm, products]);

  // Admin'de navbar gizle
  if (location.pathname.startsWith("/admin")) return null;

  // Menü yapısı
  const navItems = [
    { name: t("navbar.deals"), href: "/gunun-firsatlari" },
    { name: t("navbar.allProducts"), href: "/all-product" },
    {
      name: t("navbar.categories"),
      subcategories: dynamicCategories.length > 0 ? dynamicCategories : [
        { name: t("navbar.categoryList.vitamins"), slug: "vitaminler" },
        { name: t("navbar.categoryList.minerals"), slug: "mineraller" },
        { name: t("navbar.categoryList.herbal"), slug: "bitkisel" },
        { name: t("navbar.categoryList.immunity"), slug: "bagisiklik" },
        { name: t("navbar.categoryList.heart"), slug: "kalp-dolasim" },
        { name: t("navbar.categoryList.beauty"), slug: "cilt" },
        { name: t("navbar.categoryList.energy"), slug: "enerji" }
      ],
    },
    { name: t("navbar.about"), href: "/about" },
    { name: t("navbar.contact"), href: "/contact" },
  ];

  return (
    <>
      <motion.nav
        style={{
          height: navHeight,
          backgroundColor: navBackground,
          backdropFilter: `blur(${navBackdropBlur})`,
          WebkitBackdropFilter: `blur(${navBackdropBlur})`,
          boxShadow: navShadow
        }}
        className="relative w-full transition-all duration-300 border-b border-fecos-border/30"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-full">
          <div className="flex justify-between items-center h-full">

            {/* LOGO */}
            <Link to="/" className="flex items-center group relative z-50">
              <motion.img
                style={{ height: logoHeight }}
                src={logo}
                className="w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                alt="logo"
              />
            </Link>

            {/* DESKTOP MENU */}
            <div className="hidden lg:flex items-center space-x-12">
              {navItems.map((item, index) => (
                <div key={index} className="relative group h-full flex items-center">
                  {item.href ? (
                    <Link
                      to={item.href}
                      className="flex items-center gap-1 text-sm font-medium text-fecos-text/80 hover:text-fecos-text transition-colors tracking-tight relative"
                    >
                      {item.name}
                      {item.href === '/gunun-firsatlari' && (
                        <span className="absolute -top-1.5 -right-2.5 w-2 h-2 bg-orange-500 rounded-full border-2 border-white pointer-events-none" />
                      )}
                    </Link>
                  ) : (
                    <div className="flex items-center gap-1 text-sm font-medium text-fecos-text/80 hover:text-fecos-text cursor-pointer tracking-tight transition-colors">
                      {item.name}
                      <ChevronDown className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-all group-hover:rotate-180" />
                    </div>
                  )}

                  {item.subcategories && (
                    <div className="absolute top-[calc(100%)] left-1/2 -translate-x-1/2 w-64 pt-6 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 ease-out z-50">
                      <div className="bg-white/90 backdrop-blur-2xl rounded-2xl p-2 shadow-[0_20px_40px_-12px_rgba(0,0,0,0.1)] border border-white/20 overflow-hidden ring-1 ring-black/5">
                        <ul className="flex flex-col gap-1">
                          {item.subcategories.map((sub, si) => (
                            <li key={si}>
                              <Link
                                to={`/category/${sub.slug}`}
                                className="flex items-center justify-between px-4 py-3 text-[13px] font-medium text-fecos-text/70 hover:text-fecos-text hover:bg-black/5 rounded-xl transition-all"
                              >
                                {sub.name}
                                <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-50 -translate-x-2 group-hover:translate-x-0 transition-all" />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* SEARCH + CART + LANGUAGE */}
            <div className="hidden lg:flex items-center gap-6">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 text-xs font-semibold text-fecos-text/80 hover:text-fecos-text transition-colors border border-fecos-border/50 px-3 py-1.5 rounded-full hover:bg-black/5"
              >
                <Globe className="w-3.5 h-3.5" />
                {language === 'tr' ? 'EN' : 'TR'}
              </button>

              <div className="relative">
                <AnimatePresence>
                  {isSearchOpen ? (
                    <motion.div
                      initial={{ width: 0, opacity: 0 }}
                      animate={{ width: 240, opacity: 1 }}
                      exit={{ width: 0, opacity: 0 }}
                      className="relative overflow-hidden"
                    >
                      <input
                        type="text"
                        placeholder={t("navbar.searchPlaceholder")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-3 pr-8 py-2 bg-fecos-bg/50 border border-fecos-border rounded-full text-xs font-medium focus:outline-none focus:ring-1 focus:ring-fecos-text/20 transition-all placeholder:text-gray-400"
                        autoFocus
                        onBlur={() => !searchTerm && setIsSearchOpen(false)}
                      />
                      <X
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 cursor-pointer hover:text-fecos-text"
                        onClick={() => { setSearchTerm(""); setIsSearchOpen(false); }}
                      />
                    </motion.div>
                  ) : (
                    <motion.button
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onClick={() => setIsSearchOpen(true)}
                      className="p-2 hover:bg-black/5 rounded-full transition-colors"
                    >
                      <Search className="w-5 h-5 text-fecos-text/80" />
                    </motion.button>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {suggestions.length > 0 && isSearchOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full right-0 mt-3 w-72 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-2 z-50 ring-1 ring-black/5"
                    >
                      {suggestions.map((p) => (
                        <div
                          key={p.id}
                          className="px-4 py-3 text-sm font-medium text-fecos-text/80 cursor-pointer hover:bg-fecos-bg/80 rounded-xl transition-colors flex items-center gap-3"
                          onClick={() => {
                            navigate(`/product/${p.id}`);
                            setSearchTerm("");
                            setSuggestions([]);
                            setIsSearchOpen(false);
                          }}
                        >
                          <Search className="w-3 h-3 opacity-40" />
                          {p.name}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link to="/cart-drawer" className="relative group p-2 hover:bg-black/5 rounded-full transition-all">
                <ShoppingCart className="w-5 h-5 text-fecos-text/80 group-hover:text-fecos-text transition-colors" />
                <AnimatePresence>
                  {getCartCount() > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      className="absolute -top-0.5 -right-0.5 bg-fecos-accent text-white text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full shadow-sm"
                    >
                      {getCartCount()}
                    </motion.span>
                  )}
                </AnimatePresence>
              </Link>
            </div>

            {/* MOBILE MENU BUTTON */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 text-fecos-text active:scale-95 transition-transform"
            >
              <Menu className="w-6 h-6" />
            </button>

          </div>
        </div>
      </motion.nav>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[60]"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed left-0 top-0 w-[85%] max-w-[360px] h-screen bg-fecos-bg z-[70] shadow-2xl overflow-y-auto"
            >
              <div className="p-6 flex flex-col h-full">
                <div className="flex justify-between items-center mb-8">
                  <img src={logo} className="h-10 object-contain ml-2" alt="logo" />
                  <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 bg-white rounded-full shadow-sm">
                    <X className="w-5 h-5 text-gray-700" />
                  </button>
                </div>

                <div className="mb-6">
                  <button
                    onClick={toggleLanguage}
                    className="w-full flex items-center justify-start gap-3 p-4 bg-white rounded-2xl text-sm font-semibold text-fecos-text shadow-sm"
                  >
                    <Globe className="w-5 h-5 text-gray-500" />
                    {language === 'tr' ? t("navbar.switchToEn") : t("navbar.switchToTr")}
                  </button>
                </div>

                <div className="relative mb-8">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t("navbar.searchPlaceholder")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white rounded-2xl text-sm font-medium border-none shadow-sm focus:ring-2 focus:ring-fecos-accent/20 transition-all placeholder:text-gray-400"
                  />
                </div>

                <div className="flex flex-col gap-2 flex-1">
                  <Link
                    to="/gunun-firsatlari"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-4 bg-white rounded-2xl text-sm font-semibold text-fecos-text shadow-sm hover:scale-[1.02] active:scale-[0.98] transition-all flex justify-between items-center"
                  >
                    {t("navbar.deals")}
                    <ArrowRightIcon className="w-4 h-4 opacity-30" />
                  </Link>
                  <Link
                    to="/all-product"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-4 bg-white rounded-2xl text-sm font-semibold text-fecos-text shadow-sm hover:scale-[1.02] active:scale-[0.98] transition-all flex justify-between items-center"
                  >
                    {t("navbar.allProducts")}
                    <ArrowRightIcon className="w-4 h-4 opacity-30" />
                  </Link>

                  <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                    <button
                      className="w-full p-4 flex justify-between items-center text-sm font-semibold text-fecos-text"
                      onClick={() => setActiveMobileSubmenu(activeMobileSubmenu === "categories" ? null : "categories")}
                    >
                      {t("navbar.categories")}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${activeMobileSubmenu === "categories" ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence>
                      {activeMobileSubmenu === "categories" && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: "auto" }}
                          exit={{ height: 0 }}
                          className="overflow-hidden bg-fecos-bg/50"
                        >
                          <div className="p-2 space-y-1">
                            {navItems[2].subcategories.map((c) => (
                              <Link
                                key={c.slug}
                                to={`/category/${c.slug}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block px-4 py-3 rounded-xl text-sm text-fecos-text/70 hover:bg-white hover:text-fecos-text transition-colors"
                              >
                                {c.name}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <Link
                    to="/about"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-4 bg-white rounded-2xl text-sm font-semibold text-fecos-text shadow-sm hover:scale-[1.02] active:scale-[0.98] transition-all"
                  >
                    {t("navbar.about")}
                  </Link>

                  <Link
                    to="/contact"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-4 bg-white rounded-2xl text-sm font-semibold text-fecos-text shadow-sm hover:scale-[1.02] active:scale-[0.98] transition-all"
                  >
                    {t("navbar.contact")}
                  </Link>
                </div>

                <div className="mt-8">
                  <Link
                    to="/cart-drawer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="w-full py-4 bg-fecos-text text-white rounded-2xl font-bold flex items-center justify-center gap-2 shadow-lg hover:bg-black transition-colors"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {t("navbar.cart")} ({getCartCount()})
                  </Link>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

const ArrowRightIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

export default Navbar;
